// Описание: https://portal.fix.ru/pages/viewpage.action?pageId=118391215
// Статистика по площадкам: https://portal.fix.ru/pages/viewpage.action?pageId=139299943#id-%D0%9A%D0%B0%D0%B1%D0%B8%D0%BD%D0%B5%D1%82%D0%92%D0%9C.%D0%A1%D1%82%D0%B0%D1%82%D0%B8%D1%81%D1%82%D0%B8%D0%BA%D0%B0%D0%BF%D0%BE%D0%BF%D0%BB%D0%BE%D1%89%D0%B0%D0%B4%D0%BA%D0%B0%D0%BC-%D0%9F%D0%B0%D1%80%D1%82%D0%BD%D0%B5%D1%80%D1%81%D0%BA%D0%B0%D1%8F%D1%81%D1%81%D1%8B%D0%BB%D0%BA%D0%B0new
import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import Head from 'next/head';

import { GetStaticPaths, GetStaticProps } from 'next';

import Form from 'epn-ui/umd/dist/components/Form';
import Grid from 'epn-ui/umd/dist/components/Grid';

import * as Sentry from '@sentry/nextjs';

import BaseCopyableLink from '@components/base/BaseCopyableLink';
import AffiliateForm from '@components/pages/tools/affiliate-links/Form';
import { getSubsArray, replaceRedirectUrl } from '@components/pages/tools/affiliate-links/utils';

import WithRole, { rolesEnum } from '@HOCs/WithRole';

import api from '@api';
import { CREATE_CREATIVE_ERRORS } from '@api/errors';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import useStorage from '@hooks/useStorage';
import useI18n, { getLocalePaths } from '@i18n';
import modifyUrl from '@utils/urls/modifyURL';
import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from 'src/utils/metrics/amplitude';
import sendGaEvent from 'src/utils/metrics/ga';

import {
    IGetCreativesData,
    IGetDomainCuttersData,
    IGetLinkReductionData,
    IGetOfferDetailDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IGetUserProfileDataFormatted,
} from '@api/types';
import appConfig from '@config/app.json';
import { isGlobalDomain } from '@config/globalDomain';
import {
    aliexpressGoodsRegexp,
    aliexpressRegexp,
    createCreativeParams,
    INPUT_NAMES_BY_CREATIVE_ERROR,
    offerParams,
    placementParams,
    REF_TYPES,
    skuIdRegexp,
} from '@pages/tools/affiliate-links/config';
import { IAffiliateLinksForm } from '@pages/tools/affiliate-links/interface';
import { CustomGetStaticPathsResult, CustomGetStaticPropsResult } from '../../../../types';

const { usePlacements, useOffers, useUserProfile, useDomainCutters, useOfferDetail } = apiHooks;

const AffiliateLinksPage: React.FC = () => {
    const { t, locale } = useI18n('AffiliateLinks');
    const { t: tErrors } = useI18n('Errors');
    const { t: tTools } = useI18n('Tools');
    const router = useRouter();

    const [form] = Form.useForm<IAffiliateLinksForm>();
    const [loading, setLoading] = useState<boolean>(false);
    const [validating, setValidating] = useState(false);
    const [resultLink, setResultLink] = useState<string>('');
    const [ordFullName, setOrdFullName] = useState<string>('');
    const [article, setArticle] = useState<string>('');
    const [erid, setErid] = useState<string>('');
    const [placementError, setPlacementError] = useState<boolean>(false);
    const [linkError, setLinkError] = useState<boolean>(false);
    const [creativeOfferId, setCreativeOfferId] = useState(null);
    const [requiredPlacement, setRequiredPlacement] = useState(true);

    const [defaultOfferIdFromLs] = useStorage('affiliateLinksOfferId', appConfig.OFFER_IDS.ALI);
    const [defaultPlacementId] = useStorage('affiliateLinksPlacementId', null);

    const { offerId: defaultOfferIdFromQuery } = router.query || {};

    const formOfferId = Form.useWatch('offer', form);
    const formLink = Form.useWatch('link', form);

    const isLinkRu = formLink?.includes('.ru') || formLink?.includes('.click');
    const isAliId = +formOfferId === appConfig.OFFER_IDS.ALI;
    const isAliexpressOffer = isAliId && isLinkRu;

    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>();

    const { data: domainCutters } = useDomainCutters(
        {},
    ) as ICreateUseRequestInfiniteResponse<IGetDomainCuttersData>;

    const { data: offerDetail } = useOfferDetail<IGetOfferDetailDataFormatted>({
        urlParams: {
            id: formOfferId,
        },
        params: {
            lang: locale,
            viewRules: 'role_user',
            fields: 'creative_placement',
        },
        dependencies: [formOfferId],
        condition: !!formOfferId,
    });

    const onGetPlacements = ([data]): void => {
        if (!data?.length) {
            form.resetFields(['placement']);
            setPlacementError(offerDetail?.creativePlacement);
            return;
        }
        setPlacementError(false);
        const currentPlacement = data?.find(item => item.id === defaultPlacementId?.toString());
        const id = currentPlacement?.id || data[0].id;
        form.setFieldValue('placement', id.toString());
    };

    const onGetOffers = ([data]): void => {
        const [firstOffer] = data || [];
        form.setFieldValue('offer', firstOffer?.id);
    };

    const { data: offersData = [] } = useOffers({
        params: {
            ...offerParams,
            lang: locale,
        },
        dependencies: [locale],
        options: { onSuccess: onGetOffers },
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    const { data: placementsData, isLoading: placementsLoading } = usePlacements({
        params: { ...placementParams, offerIds: formOfferId },
        dependencies: [formOfferId],
        condition: !!formOfferId,
        options: { onSuccess: onGetPlacements },
    }) as ICreateUseRequestInfiniteResponse<IGetPlacementsDataFormatted[]>;

    useEffect((): void => {
        if (!offerDetail?.creativePlacement) {
            setPlacementError(false);
            setRequiredPlacement(false);
        } else {
            setRequiredPlacement(true);
        }
    }, [offerDetail]);

    useEffect((): void => {
        if (defaultOfferIdFromQuery && !Number.isNaN(+defaultOfferIdFromQuery)) {
            form.setFieldValue('offer', defaultOfferIdFromQuery);
        }
        if (defaultOfferIdFromLs && !Number.isNaN(+defaultOfferIdFromLs)) {
            form.setFieldValue('offer', defaultOfferIdFromQuery);
        }
    }, [defaultOfferIdFromLs, defaultOfferIdFromQuery, form]);

    const cleanLink = (productLink: string): string => {
        if (!isAliexpressOffer) return productLink;

        const [domen] = productLink.match(aliexpressRegexp);
        const [product] = productLink.match(aliexpressGoodsRegexp);
        const [skuId] = productLink.match(skuIdRegexp) || [];

        return `${domen}${product}${skuId ? `?${skuId}` : ''}`;
    };

    const createCreative = async ({
        offer,
        placement,
        link,
        name,
        subs,
    }): Promise<IGetCreativesData> => {
        try {
            let body;
            if (isAliexpressOffer) {
                body = {
                    ...createCreativeParams,
                    link,
                    material: 'lk-affiliate-links',
                    offerId: offer,
                    placementId: +placement,
                    description: name || tTools('New link'),
                    sub: subs.sub1 || undefined,
                    sub2: subs.sub2 || undefined,
                    sub3: subs.sub3 || undefined,
                    sub4: subs.sub4 || undefined,
                    sub5: subs.sub5 || undefined,
                };
            } else {
                body = {
                    ...createCreativeParams,
                    link,
                    offerId: offer,
                    description: name || tTools('New link'),
                    ...(placement && { placementId: +placement }),
                };
            }

            const { data } = (await api.creative.createCreative({
                body,
            })) as ICreateUseRequestInfiniteResponse<IGetCreativesData>;

            return data;
        } catch (err) {
            const { errors: createCreativeErrors = [] } = await err.response.json();
            const { error: errorCode } = createCreativeErrors[0];

            const errorMessage =
                CREATE_CREATIVE_ERRORS[errorCode] || CREATE_CREATIVE_ERRORS.default;

            if (INPUT_NAMES_BY_CREATIVE_ERROR[errorCode])
                form.setFields([
                    {
                        name: INPUT_NAMES_BY_CREATIVE_ERROR[errorCode],
                        errors: [tErrors(errorMessage)],
                    },
                ]);

            return undefined;
        }
    };

    const generatePartnerLink = async ({
        name,
        link,
        domainCutter,
        isNeedLinkReduction,
        offer,
        placement,
        ...subs
    }: IAffiliateLinksForm) => {
        const cutter = !isAliexpressOffer ? domainCutter?.value : undefined;

        try {
            // Получение inviteHash из профиля пользователя
            const { inviteHash } = userData || {};

            // С каким типом оффера работает
            const isRef: boolean = appConfig.REF_OFFERS_IDS.includes(+offer);
            const [, , productId] = form.getFieldValue('link')?.match(aliexpressGoodsRegexp) || [];
            /**
             * Подготавливаем ссылку из формы для отправки в API.
             * - cleanedLink необходим для отправки в API, в дальнейшем оперируем параметром link
             * - Eсли есть productId нужно убрать лишний кэш из ссылки
             */
            let cleanedLink = link;
            if (productId) {
                cleanedLink = cleanLink(link);
            }

            setCreativeOfferId(offer);

            // Создаем новый креатив
            const deeplink = await createCreative({
                offer,
                placement,
                link: cleanedLink,
                name,
                subs,
            });

            if (!deeplink) return;

            const INNText = isGlobalDomain ? 'TAX number/VAT number' : 'ИНН';
            const AdvertText = isGlobalDomain ? 'Advert' : 'Реклама';

            setOrdFullName(
                `${AdvertText}: ${deeplink?.attributes.ordClientFullName} ${INNText}: ${deeplink?.attributes.ordClientInn}`,
            );
            setArticle(`${deeplink?.attributes.aliArticle || ''}`);
            setErid(`${deeplink?.attributes.erid || ''}`);
            setCreativeOfferId(offer);

            const { code: deeplinkCode } = deeplink?.attributes;

            let partnerLink = deeplinkCode;

            // Для реф оферов не работают редиректоры поэтому берем ссылку введенную пользователем
            if (isRef) {
                partnerLink = modifyUrl(partnerLink, 'ref_type', REF_TYPES[offer]);
                partnerLink = modifyUrl(partnerLink, 'inviter', inviteHash);
                partnerLink = modifyUrl(partnerLink, 'creative_hash', deeplink.attributes.hash);
            }

            if (subs && !isAliexpressOffer) {
                const subsList = getSubsArray(subs);
                if (Array.isArray(subsList) && subsList.length > 0) {
                    subsList.forEach(item => {
                        if (typeof item?.value === 'string' && item?.value.length > 0) {
                            partnerLink = modifyUrl(partnerLink, item.key, item.value);
                        }
                    });
                }
            }

            /**
             * Сокращение ссылке
             * - TODO: isNeedLinkReduction работает только из-за того , что 'true' приходит как не пустая строка
             */
            if (isNeedLinkReduction) {
                const { data: reducedUrl } = (await api.linkReduction.reduceLink({
                    body: {
                        urlContainer: partnerLink,
                        domainCutter: cutter,
                    },
                })) as ICreateUseRequestInfiniteResponse<IGetLinkReductionData>;

                partnerLink = reducedUrl?.attributes[0]?.result || partnerLink;
            }

            const isDev: boolean = window.location.hostname.includes('epndev');
            const isNeedRedirectUrl = isDev && !isNeedLinkReduction && !isRef && !isAliexpressOffer;

            /**
             * Модифицируем ссылку для DEV окружения
             * - На девах не работают сгенерированные несокращенные ссылки, поэтому домен меняется на redirectLink, на проде замена не производится
             */
            if (isNeedRedirectUrl) {
                partnerLink = replaceRedirectUrl(partnerLink);
            }

            setResultLink(partnerLink);

            logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.linkCreate, {
                offer_id: offer,
                type: 1,
                name: !!(typeof name === 'string' && name.length > 0),
                sub: subs ? Object.values(subs).filter(item => item?.length > 0).length : 0,
            });

            sendGaEvent({
                eventCategory: 'affiliate_link',
                eventAction: 'create',
                eventLabel: 'offer_id',
                eventValue: +offer,
            });

            form.setFieldValue('link', '');
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const validateForm = async () => {
        if (linkError) return false;

        const values = form.getFieldsValue();
        if (!values.link || !values.offer || (requiredPlacement && !values.placement)) return false;

        return true;
    };

    const onSubmit = async (data: IAffiliateLinksForm): Promise<void> => {
        try {
            const isValid = await validateForm();

            if (!isValid) return;

            setLoading(true);
            await generatePartnerLink(data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Head>
                <title>
                    {t('Affiliate account')} - {t('Get affiliate link')}
                </title>
            </Head>
            <div className="mb-[24px]">
                <h2 className="font-medium text-blueGray-600 text-sub xs:text-h2">
                    {t('Get affiliate link')}
                </h2>
            </div>
            <AffiliateForm
                form={form}
                onSubmit={onSubmit}
                placements={placementsData}
                offers={offersData}
                loading={loading || validating}
                setLoading={setValidating}
                setOffer={value => form.setFieldValue('offer', value)}
                isAli={isAliexpressOffer}
                placementError={placementError}
                placementsLoading={placementsLoading}
                domainCutters={domainCutters}
                linkError={linkError}
                setLinkError={setLinkError}
                requiredPlacement={requiredPlacement}
            />

            <div className="w-full p-4 mt-8 mb-20 border border-t xs:p-6 border-blueGray-200 rounded-[6px]">
                <Grid gutter={[24, 24]}>
                    <Grid.Col xs={24} xl={24}>
                        <BaseCopyableLink
                            label={tTools('Your affiliate link')}
                            text={resultLink}
                            loading={loading}
                        />
                    </Grid.Col>

                    {creativeOfferId === appConfig.OFFER_IDS.ALI && (
                        <Grid.Col xs={24} md={12}>
                            <BaseCopyableLink
                                label={tTools('Article')}
                                text={article}
                                description={tTools('Will work only for CIS audience')}
                                tooltip={
                                    article
                                        ? ''
                                        : t(
                                              'This field will be filled in if a link to aliexpress.ru',
                                          )
                                }
                                className={!article && `opacity-50`}
                                loading={loading}
                            />
                        </Grid.Col>
                    )}

                    <Grid.Col xs={24} md={creativeOfferId === appConfig.OFFER_IDS.ALI ? 12 : 24}>
                        <BaseCopyableLink label={t('Token')} text={erid} loading={loading} />
                    </Grid.Col>

                    <Grid.Col xs={24} xl={24}>
                        <BaseCopyableLink
                            label={tTools('Advertiser')}
                            text={ordFullName}
                            description={tTools(
                                'This information is needed for advertising labeling.',
                            )}
                            loading={loading}
                        />
                    </Grid.Col>
                </Grid>
            </div>
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};

export default WithRole(AffiliateLinksPage, [rolesEnum.user]);
